import { useEffect } from 'react';
import type { Editor } from '@tiptap/react';
import type { Doc as YDoc } from 'yjs';

import { DocumentVariables } from 'containers/Tiptap/lib/header-footer-variables';
import {
  DocumentSettings,
  observeDocumentSettings,
} from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';
import { applyDocumentSettings, applySettings } from 'containers/Tiptap/hooks/use-document-settings-sync';
import { useFitPagesToWidth } from 'containers/Tiptap/hooks/use-fit-pages-to-width';
import { useResolveHeaderFooterImages } from 'containers/Tiptap/hooks/use-resolve-header-footer-images';
import { useResolveHeaderFooterVariables } from 'containers/Tiptap/hooks/use-resolve-header-footer-variables';

type SettingsSource =
  | { ydoc: YDoc | null | undefined; settings?: never }
  | { settings: DocumentSettings | null | undefined; ydoc?: never };

type UseReadOnlyPagesLayoutArgs = SettingsSource & {
  editor: Editor | null;
  contentApplied: boolean;
  variables: DocumentVariables;
  shareId?: string;
};

export function useReadOnlyPagesLayout(args: UseReadOnlyPagesLayoutArgs): void {
  const { editor, ydoc, settings, contentApplied, variables, shareId } = args;

  useEffect(() => {
    if (!editor || !contentApplied) return undefined;
    if (settings) {
      applySettings(editor, settings);
      return undefined;
    }
    if (!ydoc) return undefined;
    const apply = (): void => applyDocumentSettings(editor, ydoc);
    apply();
    return observeDocumentSettings(ydoc, apply);
  }, [editor, ydoc, settings, contentApplied]);

  useFitPagesToWidth(editor, contentApplied, ydoc);
  useResolveHeaderFooterVariables(editor, variables);
  useResolveHeaderFooterImages(editor, shareId);
}
